/**
 * Admin UI Plugin Routes
 *
 * Defines all routes for the admin UI plugin.
 * Only accessible to platform admins.
 */

import { defineRoutes } from '@lognai/plugin-sdk';

/**
 * Plugin identifier
 */
export const pluginId = 'admin';

/**
 * All routes for the admin UI plugin
 */
export const routes = defineRoutes(pluginId, '/admin', [
  // Main admin dashboard
  {
    path: '/',
    component: () =>
      import('./pages/AdminDashboard').then((m) => ({
        default: m.AdminDashboard,
      })),
    name: 'Admin',
    description: 'Platform administration dashboard',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Settings', size: 20 },
      showInNav: true,
      order: 100, // Show last in navigation
    },
  },

  // Company management (formerly tenant management)
  {
    path: '/companies',
    component: () =>
      import('./pages/CompanyListPage').then((m) => ({
        default: m.CompanyListPage,
      })),
    name: 'Companies',
    description: 'Manage companies',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Briefcase', size: 20 },
      showInNav: true,
      order: 1,
    },
  },

  // Company hierarchy view
  {
    path: '/companies/hierarchy',
    component: () =>
      import('./pages/CompanyHierarchyPage').then((m) => ({
        default: m.CompanyHierarchyPage,
      })),
    name: 'Company Hierarchy',
    description: 'View company organizational hierarchy',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },

  // Create company
  {
    path: '/companies/new',
    component: () =>
      import('./pages/CompanyCreatePage').then((m) => ({
        default: m.CompanyCreatePage,
      })),
    name: 'Create Company',
    description: 'Create a new company',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },

  // Company details/edit
  {
    path: '/companies/:companyId',
    component: () =>
      import('./pages/CompanyDetailsPage').then((m) => ({
        default: m.CompanyDetailsPage,
      })),
    name: 'Company Details',
    description: 'View and edit company details',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },

  // User management
  {
    path: '/users',
    component: () =>
      import('./pages/UserManagementPage').then((m) => ({
        default: m.UserManagementPage,
      })),
    name: 'Users',
    description: 'Manage platform users',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Users', size: 20 },
      showInNav: true,
      order: 2,
    },
  },

  // User role management
  {
    path: '/users/:userId/roles',
    component: () =>
      import('./pages/UserRoleManagementPage').then((m) => ({
        default: m.UserRoleManagementPage,
      })),
    name: 'User Roles',
    description: 'Manage user role assignments',
    requiredRoles: ['platform_admin'],
    parentPath: '/users',
    nav: {
      showInNav: false,
    },
  },

  // User direct-access management
  {
    path: '/users/:userId/access',
    component: () =>
      import('./pages/UserAccessManagementPage').then((m) => ({
        default: m.UserAccessManagementPage,
      })),
    name: 'User Access',
    description: 'Manage user direct-access assignments',
    requiredRoles: ['platform_admin'],
    parentPath: '/users',
    nav: {
      showInNav: false,
    },
  },

  // Role management
  {
    path: '/roles',
    component: () =>
      import('./pages/RoleManagementPage').then((m) => ({
        default: m.RoleManagementPage,
      })),
    name: 'Roles',
    description: 'Manage roles and permissions',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Shield', size: 20 },
      showInNav: true,
      order: 3,
    },
  },

  // Access Object management
  {
    path: '/access-objects',
    component: () =>
      import('./pages/AccessObjectManagementPage').then((m) => ({
        default: m.AccessObjectManagementPage,
      })),
    name: 'Access Objects',
    description: 'Manage access objects and permissions',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Key', size: 20 },
      showInNav: true,
      order: 4,
    },
  },

  // Partner Network management
  {
    path: '/partner-networks',
    component: () =>
      import('./pages/PartnerNetworkManagementPage').then((m) => ({
        default: m.PartnerNetworkManagementPage,
      })),
    name: 'Partner Networks',
    description: 'Manage partner relationships',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Network', size: 20 },
      showInNav: true,
      order: 5,
    },
  },

  // Partner Type management
  {
    path: '/partner-types',
    component: () =>
      import('./pages/PartnerTypeManagementPage').then((m) => ({
        default: m.PartnerTypeManagementPage,
      })),
    name: 'Partner Types',
    description: 'Manage partner type definitions',
    requiredRoles: ['platform_admin'],
    parentPath: '/partner-networks',
    nav: {
      showInNav: false,
    },
  },

  // AI / Agents settings
  {
    path: '/ai-agents',
    component: () =>
      import('./pages/AiAgentsSettingsPage').then((m) => ({
        default: m.AiAgentsSettingsPage,
      })),
    name: 'AI / Agents',
    description: 'Configure agent-writes behavior',
    requiredRoles: ['platform_admin'],
    parentPath: '/',
    nav: {
      icon: { name: 'Sparkles', size: 20 },
      showInNav: true,
      order: 8,
    },
  },

  // Task assignment policy settings
  {
    path: '/task-assignment-policy',
    component: () =>
      import('./pages/TaskAssignmentPolicyPage').then((m) => ({
        default: m.TaskAssignmentPolicyPage,
      })),
    name: 'Task Assignment Policy',
    description: 'Control which roles can assign tasks to which roles',
    permissions: ['admin:company:update'],
    parentPath: '/',
    nav: {
      icon: { name: 'ShieldCheck', size: 20 },
      showInNav: true,
      order: 9,
    },
  },

  // ============================================================================
  // Company-scoped routes (from company-ui plugin - permission-based access)
  // ============================================================================

  // Company members / roles / invitations / hierarchy are now in-page tabs on
  // the company-detail page (?tab=members etc.), not standalone routes. Only the
  // create/edit sub-pages reached from within those tabs remain as routes below.

  // Company role detail (edit a role — reached from the company's Roles tab)
  {
    path: '/companies/:companyId/roles/:roleId',
    component: () =>
      import('./pages/RoleDetailPage').then((m) => ({
        default: m.RoleDetailPage,
      })),
    name: 'Role Details',
    description: 'View and edit role',
    parentPath: '/companies/:companyId',
    nav: {
      showInNav: false,
    },
  },

  // Company member detail
  {
    path: '/companies/:companyId/members/:memberId',
    component: () =>
      import('./pages/MemberDetailPage').then((m) => ({
        default: m.MemberDetailPage,
      })),
    name: 'Member Details',
    description: 'View and edit member',
    parentPath: '/companies/:companyId',
    nav: {
      showInNav: false,
    },
  },

  // Create invitation (reached from the company's Invitations tab)
  {
    path: '/companies/:companyId/invitations/new',
    component: () =>
      import('./pages/InvitationCreatePage').then((m) => ({
        default: m.InvitationCreatePage,
      })),
    name: 'Invite Member',
    description: 'Send an invitation to join the company',
    parentPath: '/companies/:companyId',
    nav: {
      showInNav: false,
    },
  },

  // NOTE: per-company partnership routes removed — partnerships are managed
  // centrally on the Partner Networks page (/admin/partner-networks).
]);

/**
 * Public routes (no auth required)
 * For accepting/declining invitations via token
 */
export const publicRoutes = defineRoutes(pluginId, '/invite', [
  {
    path: '/accept',
    component: () =>
      import('./pages/InvitationAcceptPage').then((m) => ({
        default: m.InvitationAcceptPage,
      })),
    name: 'Accept Invitation',
    description: 'Accept an invitation to join a company',
    requiresAuth: false,
    nav: {
      showInNav: false,
    },
  },
]);
